import React, { useEffect, useRef, useState, useCallback } from 'react';
import { message } from 'antd';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';

import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, EditOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNavigationStore } from '@/common/store';
import styled, { keyframes } from 'styled-components';
import { Button } from '@/common/components/button/Button';
import Section from '@/common/components/section';
import { LabelInput } from '@/common/components/input/LabelInput';
import ViewSwitcher from '@/common/components/viewSwitcher/ViewSwitcher';
import { OnboardingInfoProps, onboardingService } from '@/services/onboardingService';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { toastUtils } from '@/common/utils/toastUtils';
import { formatPhoneNumber, formatBusinessNumber } from '@/common/utils/textFormat';
import FileUpload from '@/common/components/fileUpload/FileUpload';
import { commonService } from '@/services/commonService';
import Avatar from '@/common/components/avatar/Avatar';
import config from '@/config';
import { aiService } from '@/services/aiService';
import { companyService } from '@/services/companyService';
import { extractDate } from '@/common/utils/dateFormat';
import { fileService } from '@/services/fileService';
import { useAuthStore } from '@/store/useAuthStore';
import { authService } from '@/services/authService';
import LoadingModal from '@/common/components/modal/LoadingModal';
import { getBirthGender, validateLoginId, validatePassword } from '@/common/utils/commonUtil';
import { 
  Check, 
  ChevronRight, 
  Shield, 
  Building2, 
  User, 
  FileCheck, 
  Sparkles,
  Rocket,
  PartyPopper,
  ArrowRight
} from 'lucide-react';

/**
 * 회사 정보 설정 인터페이스
 */
interface CompanySettings {
  logo: File | null;
  englishCode: string;
}

/**
 * 사업자 정보 인터페이스
 */
interface BusinessInfo {
  companyName: string;
  businessNumber: string;
  representative: string;
  corporateNumber: string;
  establishedDate: string;
}

interface FormData {
  notificationId?: string;
  companyEmail?: string;
  companyEmailVerified?: boolean;
  companyCode?: string;
  isVerified?: boolean;
  isPreStartup?: boolean;
  isCeo?: boolean;
  loginId?: string;
  companyName?: string;
  companyNameEn?: string;
  businessNumber?: string;
  corporateNumber?: string;
  isBusinessVerified?: boolean;
  foundingDate?: string;
  residentNumber?: string;
  ceoName?: string;
  ceoEmail?: string;
  newLogoFile?: File;
  logo?: number;
  businessRegistryFileId?: number;
  name?: string;
  phone?: string;
  email?: string;
  firstEmail?: string;
  secondEmail?: string;
  emailVerified?: boolean;
  password?: string;
  confirmPassword?: string;
  userDi?: string;
  userCi?: string;
  token?: string;
  isCompanyEmail?: boolean;
}

/**
 * 법인번호 포맷팅 함수 (123456-1234567)
 */
const formatCorporateNumber = (value: string): string => {
  const numbers = value.replace(/[^0-9]/g, '');
  if (numbers.length <= 6) return numbers;
  return `${numbers.slice(0, 6)}-${numbers.slice(6, 13)}`;
};

/**
 * 설립일 포맷팅 함수 (yyyy-MM-dd)
 */
const formatEstablishmentDate = (value: string): string => {
  const numbers = value.replace(/[^0-9]/g, '');
  if (numbers.length <= 4) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
  return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)}`;
};

/**
 * 포트폴리오 초대 페이지 컴포넌트
 * 4단계의 초대 프로세스를 제공합니다.
 */
const InvitationPortfolioPage: React.FC = () => {
  
  const navigate = useNavigate();
  const {  } = useNavigationStore();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const notificationId = searchParams.get('n_id') || '';
  const domain = config.domain;

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [invitationInfo, setInvitationInfo] = useState<OnboardingInfoProps | null>(null);

  const [formData, setFormData] = useState<FormData>({
    notificationId: notificationId
  });

  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    logo: null,
    englishCode: ''
  });

  // 인증 관련 상태
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isBusinessVerified, setIsBusinessVerified] = useState<boolean|undefined>(undefined);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  
  // 인증된 사업자 정보
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  
  // 성공 단계 카운트다운 상태
  const [countdown, setCountdown] = useState<number>(10);

  // 중복 사용자 모달 상태
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const userDiRef = useRef<{di: string, ci: string, token: string} | undefined>(undefined);

  

  // 임시 간편인증 모달 상태
  const [showTempAuthModal, setShowTempAuthModal] = useState<boolean>(false);
  const [tempAuthName, setTempAuthName] = useState<string>('');
  const [tempAuthNameError, setTempAuthNameError] = useState<string>('');
  const [tempPhoneNumber, setTempPhoneNumber] = useState<string>('');
  const [tempPhoneError, setTempPhoneError] = useState<string>('');

  // 유효성 검사 에러 상태
  const [validationErrors, setValidationErrors] = useState<{
    loginId?: string | null;
    secondEmail?: string | null;
    password?: string | null;
    confirmPassword?: string | null;
    companyCode?: string | null;
    isBusinessVerified?: string | null;
    businessNumber?: string | null;
    ceoName?: string | null;
  }>({});

  // 회사코드 중복검사 상태
  const [isCheckingCompanyCode, setIsCheckingCompanyCode] = useState<boolean>(false);
  const [companyCodeChecked, setCompanyCodeChecked] = useState<boolean>(false);

  // 이메일 중복검사 상태 추가
  const [isCheckingLoginId, setIsCheckingLoginId] = useState<boolean>(false);
  const [emailChecked, setEmailChecked] = useState<boolean>(false);

  const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false);
  const [loginIdChecked, setLoginIdChecked] = useState<boolean>(false);

  const goToPortfolioOnboarding = (code: string, onboardingStatus?: string) => {
    window.location.replace(`${config.startupClientUrl}/${code}/invitation/portfolio${onboardingStatus ? `?onboardingStatus=${onboardingStatus}` : ''}`);
  }

  /**
   * 받침 여부 확인 함수 (은/는 조사 자동 선택)
   * @param text 확인할 텍스트
   * @returns 받침이 있으면 '은', 없으면 '는'
   */
  const getParticle = (text: string): string => {
    if (!text) return '는';
    
    const lastChar = text[text.length - 1];
    const charCode = lastChar.charCodeAt(0);
    
    // 한글인지 확인 (가-힣)
    if (charCode >= 0xAC00 && charCode <= 0xD7A3) {
      // 받침이 있으면 (charCode - 0xAC00) % 28 !== 0
      const hasBatchim = (charCode - 0xAC00) % 28 !== 0;
      return hasBatchim ? '은' : '는';
    }
    
    // 한글이 아니면 기본값 '는' 반환
    return '는';
  };

  /**
   * 이메일 유효성 검사
   */
  const validateEmail = (email: string): string | null => {
    if (!email) return '이메일을 입력해주세요.';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return '올바른 이메일 형식을 입력해주세요.';
    }
    
    return null;
  };

  /**
   * 회사 이메일과 개인 이메일 동일성 검사
   */
  const validateEmailDuplication = (value: string): string | null => {
    if (!formData.firstEmail) return null;
    
    if (formData.firstEmail === value) {
      return '회사 이메일과 개인 이메일은 다른 주소여야 합니다.';
    }
    
    return null;
  };


  /*   * 비밀번호 확인 유효성 검사
   */
  const validateConfirmPassword = (password: string, confirmPassword: string): string | null => {
    if (!confirmPassword) return '비밀번호 확인을 입력해주세요.';
    
    if (password !== confirmPassword) {
      return '비밀번호가 일치하지 않습니다.';
    }
    
    return null;
  };

  /**
   * 회사코드 유효성 검사
   */
  const validateCompanyCode = (code: string): string | null => {
    if (!code) return '회사코드를 입력해주세요.';
    
    // 공백 검사
    if (code.includes(' ')) {
      return '회사코드에는 공백을 포함할 수 없습니다.';
    }
    
    // 영문, 숫자만 허용 (선택사항)
    const codeRegex = /^[a-zA-Z0-9]+$/;
    if (!codeRegex.test(code)) {
      return '회사코드는 영문과 숫자만 입력 가능합니다.';
    }
    
    
    return null;
  };

  /**
   * 실시간 유효성 검사 처리
   */
  const handleValidation = (field: string, value: string) => {
    let error: string | null = null;
    
    switch (field) {
      case 'loginId':
        error = validateLoginId(value) as string | null;
        setLoginIdChecked(false);
        break;
      case 'secondEmail':
        error = value.length === 0 ? `${formData.isCompanyEmail ? "개인 이메일을 입력해주세요." : "회사 이메일을 입력해주세요."}` : validateEmailDuplication(value) || validateEmail(value);
        setEmailChecked(false);
        break;
      case 'password':
        error =  validatePassword(value) as string | null;
        // 비밀번호가 변경되면 확인 비밀번호도 다시 검사
        if (formData.confirmPassword) {
          const confirmError = validateConfirmPassword(value, formData.confirmPassword);
          setValidationErrors(prev => ({
            ...prev,
            confirmPassword: confirmError
          }));
        }
        break;
      case 'confirmPassword':
        error = validateConfirmPassword(formData.password || '', value);
        break;
      case 'companyCode':
        error = validateCompanyCode(value);
        // 회사코드가 변경되면 중복검사 상태 초기화
        setCompanyCodeChecked(false);
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  /**
   * 3단계 폼 전체 유효성 검사
   */
  const validateRegistrationForm = (): boolean => {
    const errors: typeof validationErrors = {};
    
    
    if (formData.secondEmail) {
      if(formData.secondEmail.length === 0) {
        errors.secondEmail = `${formData.isCompanyEmail ? "개인 이메일을 입력해주세요." : "회사 이메일을 입력해주세요."}`;
      }
      errors.secondEmail = validateEmail(formData.secondEmail);
      // 이메일 중복성 검사
      if (!errors.secondEmail) {
        const duplicationError = validateEmailDuplication(formData.secondEmail);
        if (duplicationError) {
          errors.secondEmail = duplicationError;
        }
      }
    }else{
      errors.secondEmail = `${formData.isCompanyEmail ? "개인 이메일을 입력해주세요." : "회사 이메일을 입력해주세요."}`;
    }
    
    // 비밀번호 검사
    errors.password = validatePassword(formData.password || '');
    errors.confirmPassword = validateConfirmPassword(formData.password || '', formData.confirmPassword || '');
    console.log('errors', errors);  
    setValidationErrors(errors);
    
    // 에러가 없고 이메일 중복검사가 완료되었는지 확인
    const hasErrors = Object.values(errors).some(error => error !== null);
    const isEmailCheckRequired = formData.secondEmail && !emailChecked;
    
    return !hasErrors && !isEmailCheckRequired;
  };


  /**
   * 4단계 사업자 정보 인증 유효성 검사
   * @param data 
   * @returns 
   */
  const validateBusinessInfo = (data: BusinessInfo) => {
    const errors: typeof validationErrors = {};

    if(invitationInfo?.businessNumber !== data.businessNumber) {
      errors.businessNumber = '초대된 사업자번호와 일치하지 않습니다.';
    }

    return { isValid: !Object.values(errors).some(error => error !== null), errors };
  }



  const validateCompanySettingsForm = (): boolean => {
    // 상태 변경 없이 검증만 수행
    const hasCompanyCodeError = companySettings.englishCode ? !!validateCompanyCode(companySettings.englishCode) : true;
    const hasBusinessVerificationError = !isBusinessVerified;
    const hasCodeCheckError = !companyCodeChecked;
    
    return !hasCompanyCodeError && !hasBusinessVerificationError && !hasCodeCheckError;
  };

  /**
   * 4단계 폼 검증 및 누락 항목 알림
   */
  const handleCompanySettingsNext = () => {
    const missingItems: string[] = [];
    
    // 회사코드 검사
    if (!companySettings.englishCode) {
      missingItems.push('회사코드를 입력해주세요');
    } else {
      const codeError = validateCompanyCode(companySettings.englishCode);
      if (codeError) {
        missingItems.push(`회사코드: ${codeError}`);
      } else if (!companyCodeChecked) {
        missingItems.push('회사코드 중복확인을 완료해주세요');
      }
    }
    
    // 사업자 정보 인증 검사
    if (!isBusinessVerified) {
      missingItems.push('사업자 정보 인증을 완료해주세요');
    }
    
    // 로고 업로드는 선택사항이므로 검사하지 않음
    
    if (missingItems.length > 0) {
      const message = `다음 항목을 확인해주세요:\n• ${missingItems.join('\n• ')}`;
      toastUtils.error(message);
      return;
    }
    
    // 모든 검증 통과 시 다음 단계로 이동
    createCompanyAndUser();
  };


  const createCompanyAndUser = async () => {
    try {
      if(companySettings.logo) {
        const logoFileResult = await fileService.uploadFileImage([companySettings.logo]);
        if(logoFileResult) {
          formData.logo = logoFileResult[0].id;
        }
      }

      const response = await companyService.registerUserAndCompany({
        ...formData, 
        isPresetColor: false, 
        isBusinessVerified: true, 
        email: formData.isCompanyEmail ? formData.secondEmail : formData.firstEmail, 
        companyEmail: formData.isCompanyEmail ? formData.firstEmail : formData.secondEmail,
        companyCode: companySettings.englishCode,
      });
      if(response.success) {
        toastUtils.success('회사 및 유저 생성이 완료되었습니다.');
        handleNext(); // 다음 단계로 이동
      } else {
        toastUtils.error(response.message || '회사 및 유저 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('회사 및 유저 생성 중 오류:', error);
    }
  }

  /**
   * 다음 단계로 이동 (유효성 검사 포함)
   */
  const handleNext = () => {
    // 3단계에서는 간편인증 후 유저 정보 설정 유효성 검사
    if (currentStep === 2) {
      // 이메일 중복검사가 필요한 경우 체크

      if (formData.secondEmail && formData.secondEmail.length > 0 && !emailChecked) {
        toastUtils.error('이메일 중복확인을 완료해주세요.');
        return;
      }
      
      if (!validateRegistrationForm()) {
        // 더 구체적인 에러 메시지 제공
        const missingItems: string[] = [];

        if (validationErrors.loginId) {
          missingItems.push(`아이디: ${validationErrors.loginId}`);
        }
        if (validationErrors.secondEmail) {
          missingItems.push(`이메일: ${validationErrors.secondEmail}`);
        }
        if (validationErrors.password) {
          missingItems.push(`비밀번호: ${validationErrors.password}`);
        }
        if (validationErrors.confirmPassword) {
          missingItems.push(`비밀번호 확인: ${validationErrors.confirmPassword}`);
        }
        
        if (missingItems.length > 0) {
          const message = `다음 항목을 확인해주세요:\n• ${missingItems.join('\n• ')}`;
          toastUtils.error(message);
        } else {
          toastUtils.error('입력한 정보를 다시 확인해주세요.');
        }
        return;
      }
    }
    
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  /**
   * 이전 단계로 이동
   */
  const handleBack = () => {
    console.log('formdAta', formData);
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };


  /**
   * 회사 정보 변경 핸들러
   */
  const handleCompanyChange = (field: keyof CompanySettings, value: any) => {
    setCompanySettings(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 회사코드 변경 시 유효성 검사
    if (field === 'englishCode') {
      handleValidation('companyCode', value);
    }
  };

  /**
   * 회사코드 중복 검사
   */
  const handleCheckCompanyCode = async () => {
    const code = companySettings.englishCode;
    
    // 먼저 유효성 검사
    const validationError = validateCompanyCode(code);
    if (validationError) {
      setValidationErrors(prev => ({
        ...prev,
        companyCode: validationError
      }));
      return;
    }
    
    setIsCheckingCompanyCode(true);
    
    try {
      const result = await commonService.checkDuplicateValue('companyCode', code);
      
      if (result.exists) {
        setValidationErrors(prev => ({
          ...prev,
          companyCode: '이미 사용 중인 회사코드입니다.'
        }));
        setCompanyCodeChecked(false);
        message.error('이미 사용 중인 회사코드입니다.');
      } else {
        setValidationErrors(prev => ({
          ...prev,
          companyCode: null
        }));
        setCompanyCodeChecked(true);
        message.success('사용 가능한 회사코드입니다.');
      }
    } catch (error) {
      console.error('회사코드 중복 검사 오류:', error);
      message.error('중복 검사 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsCheckingCompanyCode(false);
    }
  };


  // 간편인증 처리
  const handleCertify = async (certifyData: InisisAuthResult) => {
    try {
      
      if(certifyData.isDuplicate) {
        userDiRef.current = {di: certifyData.userDi, ci: certifyData.userCi, token: certifyData.token} ;
        setShowDuplicateModal(true);
        return;
      }
      
      console.log('중복 사용자 아님, 이름 비교 시작');
      
      const userName = (certifyData.userName || '').replace(/\s/g, '').toUpperCase();
      const invitedNames = (invitationInfo?.invitedName || []).map(name => name.replace(/\s/g, '').toUpperCase());
      const birthGender = getBirthGender(certifyData.userBirth, certifyData.userGender);

      if (!userName || invitedNames.length === 0) {
        toastUtils.error('초대된 사용자와 일치하지 않습니다.');
        return;
      }

      const matchedIndex = invitedNames.findIndex(name => name === userName);
      if (matchedIndex === -1) {
        toastUtils.error('초대된 사용자와 일치하지 않습니다.');
        return;
      }

      const matchedEmail = invitationInfo?.invitedEmail?.[matchedIndex] || '';

      setFormData((prev: any) => ({
        ...prev,
        name: certifyData.userName,
        phone: formatPhoneNumber(certifyData.userPhone || ''),
        firstEmail: matchedEmail,
        isCompanyEmail: true,
        userDi: certifyData.userDi,
        userCi: certifyData.userCi,
        token: certifyData.token,
        residentNumber: birthGender+'******',
        isCeo: invitationInfo?.ceoName.includes(certifyData.userName),
      }));

      handleNext();
    } catch (error) {
      console.error('인증 처리 중 오류 발생:', error);
      toastUtils.error('인증 처리 중 오류가 발생했습니다.');
    } finally {
      
    }
  };


  const checkUserLogin = async (businessNumber: string, onboardingStatus?: string) => {
    if(!user) return;

    const company = user.companyUsers.find(company => company.company.businessNumber === businessNumber); 
    if(company) {
      goToPortfolioOnboarding(company.company.code, onboardingStatus);
    } 
  }

  /**
   * 중복 사용자 로그인 핸들러
   */
  const handleDuplicateUserLogin = async () => {
    setShowDuplicateModal(false);
    if(!userDiRef.current) return;

    const response = await authService.loginWithUserDi(userDiRef.current.di, userDiRef.current.ci, userDiRef.current.token);
    if(response && response.success) {
      authService.setUserProfile().then(response => {
        if(response.success && invitationInfo) {
          checkUserLogin(invitationInfo.businessNumber, invitationInfo.onboardingStatus);
        }
      });
    }
  };

  /**
   * 중복 사용자 모달 닫기 핸들러
   */
  const handleDuplicateModalClose = () => {
    setShowDuplicateModal(false);
    userDiRef.current = undefined;
  };

  /**
   * 랜덤 문자열 생성 함수 (현재 시간 기반)
   */
  const generateRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    const now = Date.now(); // 현재 시간 (밀리초)
    const randomSeed = Math.random().toString(36).substring(2); // 추가 랜덤성
    
    // 현재 시간과 랜덤 시드를 조합하여 고유성 증대
    const timeBasedSeed = (now + Math.random() * 1000000).toString(36);
    
    let result = '';
    
    // 시간 기반 문자열의 일부를 포함
    const timePart = timeBasedSeed.substring(0, Math.min(10, length));
    result += timePart;
    
    // 나머지는 랜덤하게 생성
    const remainingLength = length - timePart.length;
    for (let i = 0; i < remainingLength; i++) {
      // 현재 시간과 인덱스를 활용한 시드
      const seed = (now + i + Math.random() * 1000) % chars.length;
      result += chars.charAt(Math.floor(seed));
    }
    
    // 결과를 섞어서 패턴을 더 무작위로 만들기
    return result.split('').sort(() => Math.random() - 0.5).join('');
  };

  /**
   * 임시 간편인증 모달 열기
   */
  const handleTempAuthModalOpen = () => {
    setShowTempAuthModal(true);
    setTempAuthName('');
    setTempAuthNameError('');
    setTempPhoneNumber('');
    setTempPhoneError('');
  };

  /**
   * 임시 간편인증 모달 닫기
   */
  const handleTempAuthModalClose = () => {
    setShowTempAuthModal(false);
    setTempAuthName('');
    setTempAuthNameError('');
    setTempPhoneNumber('');
    setTempPhoneError('');
  };

  /**
   * 핸드폰번호 유효성 검사
   */
  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^010-\d{4}-\d{4}$/;
    return phoneRegex.test(phone);
  };

  /**
   * 핸드폰번호 포맷팅
   */
  const formatTempPhoneNumber = (value: string): string => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  /**
   * 임시 간편인증 처리
   */
  const handleTempAuthSubmit = () => {
    let hasError = false;

    if (!tempAuthName.trim()) {
      setTempAuthNameError('이름을 입력해주세요.');
      hasError = true;
    }

    if (!tempPhoneNumber) {
      setTempPhoneError('핸드폰번호를 입력해주세요.');
      hasError = true;
    } else if (!validatePhoneNumber(tempPhoneNumber)) {
      setTempPhoneError('올바른 핸드폰번호 형식을 입력해주세요. (010-0000-0000)');
      hasError = true;
    }

    if (hasError) return;

    // 현재 시간을 기반으로 한 고유한 userDi와 token 생성
    const now = Date.now();
    const performanceTime = performance.now();
    const phoneHash = tempPhoneNumber.replace(/-/g, '').split('').reverse().join(''); // 핸드폰번호 역순
    
    // userDi: 현재 시간 + 핸드폰번호 + 랜덤값을 조합한 88자리
    const userDiSeed = `${now}_${performanceTime}_${phoneHash}_${Math.random()}_${Date.now()}`;
    const userDiBase64 = btoa(userDiSeed + Math.random().toString(36)).replace(/[+=]/g, '');
    const randomUserDi = (userDiBase64 + generateRandomString(100)).substring(0, 88);

    // token: 더 짧지만 고유한 24자리
    const tokenSeed = `${now}_${Math.random()}_${performanceTime}`;
    const tokenBase64 = btoa(tokenSeed).replace(/[+=]/g, '');
    const randomToken = (tokenBase64 + generateRandomString(30)).substring(0, 24);

    console.log('생성된 고유 값들:', {
      userDi: randomUserDi,
      token: randomToken,
      timestamp: new Date(now).toISOString()
    });

    const tempAuthData: InisisAuthResult = {
      userName: tempAuthName.trim(),
      userPhone: tempPhoneNumber.replace(/-/g, ''),
      userDi: randomUserDi,
      userCi: randomUserDi,
      token: randomToken,
      isDuplicate: false,
      userBirth: '',
      userGender: ''
    };

    setShowTempAuthModal(false);
    handleCertify(tempAuthData);
  };

  const autoCheckCompanyStatus = async (data: BusinessInfo) => {
    try {
      console.log('autoCheckCompanyStatus', data);
      const representative = data.representative.split(",").map(name => name.trim());
      let isExistCompany = false;
      for(let i = 0; i < representative.length; i++) {
        console.log('representative[i]', representative[i]);
        const statusResponse = await companyService.checkCompanyStatus({
          companyName: data.companyName,
          ceoName: representative[i],
          businessNumber: data.businessNumber.replace(/-/g, ''),
          corporateNumber: data.corporateNumber.replace(/-/g, ''),
          establishedDate: extractDate(data.establishedDate).replace(/-/g, '')
        });

        console.log('statusResponse', statusResponse);
        if(statusResponse.isExistCompany) {
          isExistCompany = true;
          setFormData((prev: any) => ({
            ...prev,
            ceoName: representative[i],
          }));
          break;
        }
      }

      if(isExistCompany) {
        setIsBusinessVerified(true);
        setFormData((prev: any) => ({
          ...prev,
          businessNumber: data.businessNumber,
          corporateNumber: data.corporateNumber,
          foundingDate: extractDate(data.establishedDate),
          isBusinessVerified: true,
        }));
      } else {
        
        toastUtils.error('사업자 정보 인증이 실패하였습니다. 법인등록번호 또는 설립일이 정확한지 확인해주세요.');
        setIsBusinessVerified(false);
      }
    } catch (error) {
      console.error('자동 회사 상태 확인 중 오류:', error);
    }
  };


  /**
   * 사업자 정보 인증 API 호출
   */
  const handleVerification = async () => {
    // 이미 처리 중인 경우 더블 클릭 방지
    if (isVerifying) {
      return;
    }

    if (!uploadedFile) {
      message.error('사업자등록증 파일을 먼저 업로드해주세요.');
      return;
    }
    
    setIsVerifying(true);
    try {
      // 실제 API 호출 부분
      const fileResult = await fileService.uploadFile([uploadedFile]);

      if(!fileResult) return toastUtils.error('파일 업로드에 실패했습니다. 파일을 확인해주세요.');

      setFormData((prev: any) => ({
        ...prev,
        businessRegistryFileId: fileResult[0].id
      }));
      
      const result = await aiService.getAnalyzeBusinessRegistration({
        fileId: fileResult[0].id,
        portfolioId: invitationInfo?.id
      });

      if(result) {
        const data = {
          companyName: result.companyName,
          businessNumber: result.businessNumber,
          representative: result.representative,
          corporateNumber: result.corporateNumber,
          establishedDate: result.establishmentDate,
        }
        setFormData((prev) => ({
          ...prev,
          businessNumber: result.businessNumber,
          corporateNumber: result.corporateNumber,
          foundingDate: result.establishmentDate,
        }));
        setBusinessInfo(data);

        if(data.companyName && data.businessNumber && data.representative && data.corporateNumber && data.establishedDate) {
          const validation = validateBusinessInfo(data);
          console.log('validateBusinessInfo', validation);
          if(!validation.isValid) {
            setValidationErrors(validation.errors);
            setIsBusinessVerified(false);
          }else{
            setValidationErrors({});
            await autoCheckCompanyStatus(data);
          }
        }else{
          setIsBusinessVerified(false);
          return;
        }
      } else {
        toastUtils.error('ai 분석에 실패했습니다. 파일을 확인해주세요.');
        setIsBusinessVerified(false);
      }  
    } catch (error) {
      console.error('ai 분석 API 오류:', error);
      toastUtils.error('ai 분석 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsVerifying(false);
    }
  };

  
  /**
   * 사업자 정보의 완성도 체크
   */
  const isBusinessInfoComplete = (info: BusinessInfo | null): boolean => {
    if (!info) return false;
    return !!(info.companyName && info.businessNumber && info.representative && 
             info.corporateNumber && info.establishedDate);
  };

  /**
   * 현재 사업자 인증 상태 확인
   * 1: 인증완료, 2: AI 정보 부족, 3: 초대정보와 다름
   */
  const getBusinessVerificationState = (): 1 | 2 | 3 => {
    if (isBusinessVerified === true) return 1;
    
    if (validationErrors.businessNumber) return 3;
    
    if (!isBusinessInfoComplete(businessInfo)) return 2;
    
    return 2;
  };

  /**
   * 1단계: 웰컴 화면
   */
  const renderWelcomeStep = () => (
    <WelcomeContainer>
      <WelcomeContent>
        <WelcomeTextSection>
          <WelcomeInvestorBadge>
            {invitationInfo?.logo ? (
              <LogoImg src={invitationInfo.logo} alt='logo' />
            ) : (
              <LogoBox>
                <span>{invitationInfo?.investor.slice(0, 1).toUpperCase()}</span>
              </LogoBox>
            )}
            <span>{invitationInfo?.investor}</span>
          </WelcomeInvestorBadge>
          
          <WelcomeTitle>
            
            <WelcomeWave role="img" aria-label="손 인사">👋</WelcomeWave> 반갑습니다.
          </WelcomeTitle>
          
          <WelcomeDescription>
            <strong>{invitationInfo?.investor}</strong>{invitationInfo?.investor ? getParticle(invitationInfo.investor) : '는'} 스타트업의 사업지원 및 투자, 그리고 효율적인 소통을 위해<br/>                      
            <WelcomeHighlight> 통합 데이터 플랫폼 '팩트시트 (Factsheet)'</WelcomeHighlight>서비스를 도입하였습니다.<br/>
            아래 버튼 클릭 후 회원 가입 및 스타트업 등록 진행을 완료해 주세요.
          </WelcomeDescription>
          
          <WelcomeFeatures>
            <WelcomeFeatureItem>
              <WelcomeFeatureIcon><FileCheck size={18} /></WelcomeFeatureIcon>
              <span>자료 요청 및 제출</span>
            </WelcomeFeatureItem>
            <WelcomeFeatureItem>
              <WelcomeFeatureIcon><Building2 size={18} /></WelcomeFeatureIcon>
              <span>투자 심사 프로세스</span>
            </WelcomeFeatureItem>
            <WelcomeFeatureItem>
              <WelcomeFeatureIcon><User size={18} /></WelcomeFeatureIcon>
              <span>주주 커뮤니케이션</span>
            </WelcomeFeatureItem>
          </WelcomeFeatures>
          
          <WelcomeStartButton onClick={handleNext}>
            <span>팩트시트 시작하기</span>
            <ArrowRight size={20} />
          </WelcomeStartButton>
        </WelcomeTextSection>
        
        <WelcomeImageSection>
          <WelcomeIllustration>
            <WelcomeGlowOrb $delay={0} />
            <WelcomeGlowOrb $delay={2} />
            <WelcomeGlowOrb $delay={4} />
            <WelcomeIconGrid>
              <WelcomeFloatingIcon $delay={0}><Rocket size={32} /></WelcomeFloatingIcon>
              <WelcomeFloatingIcon $delay={1}><Sparkles size={28} /></WelcomeFloatingIcon>
              <WelcomeFloatingIcon $delay={2}><Building2 size={30} /></WelcomeFloatingIcon>
            </WelcomeIconGrid>
          </WelcomeIllustration>
        </WelcomeImageSection>
      </WelcomeContent>
    </WelcomeContainer>
  );

  /**
   * 2단계: 초대 정보 확인
   */
  const renderInvitationConfirmStep = () => (
    <CenteredContainer>
      <ContentWrapper>
        {/* 스텝 인디케이터 */}
        <StepIndicator>
          <StepItem $isActive={true} $isCurrent={true}>
            <StepCircle $isActive={true} $isCurrent={true}>1</StepCircle>
            <StepLabel $isActive={true}>본인 확인</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>2</StepCircle>
            <StepLabel $isActive={false}>정보 입력</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>3</StepCircle>
            <StepLabel $isActive={false}>기업 설정</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>4</StepCircle>
            <StepLabel $isActive={false}>완료</StepLabel>
          </StepItem>
        </StepIndicator>

        <ContentCard>
          <CardHeader>
            <CardIconWrapper>
              <Shield size={24} />
            </CardIconWrapper>
            <div>
              <CardTitle>본인 확인이 필요합니다</CardTitle>
              <CardDescription>
                팩트시트는 신뢰할 수 있는 협업 환경을 위해 본인 인증을 진행합니다.
              </CardDescription>
            </div>
          </CardHeader>

          <InvitationInfoCard>
            <InvitationInfoTitle>
              <Building2 size={18} />
              <span>초대 받은 정보</span>
            </InvitationInfoTitle>
            <InvitationInfoGrid>
              <InvitationInfoItem>
                <InvitationInfoLabel>기업명</InvitationInfoLabel>
                <InvitationInfoValue>{invitationInfo?.name}</InvitationInfoValue>
              </InvitationInfoItem>
              <InvitationInfoItem>
                <InvitationInfoLabel>사업자등록번호</InvitationInfoLabel>
                <InvitationInfoValue>{invitationInfo?.businessNumber}</InvitationInfoValue>
              </InvitationInfoItem>
              <InvitationInfoItem style={{ gridColumn: '1 / -1' }}>
                <InvitationInfoLabel>초대자 ({invitationInfo?.invitedName?.length || 0}명)</InvitationInfoLabel>
                <InvitedUserList>
                  {invitationInfo?.invitedName?.map((name, idx) => (
                    <InvitedUserCard key={idx}>
                      <InvitedUserAvatar>{name.charAt(0)}</InvitedUserAvatar>
                      <InvitedUserInfo>
                        <InvitedUserName>{name}</InvitedUserName>
                        <InvitedUserEmail>{invitationInfo?.invitedEmail?.[idx] || '-'}</InvitedUserEmail>
                      </InvitedUserInfo>
                    </InvitedUserCard>
                  ))}
                </InvitedUserList>
              </InvitationInfoItem>
            </InvitationInfoGrid>
          </InvitationInfoCard>

          <AuthButtonWrapper>
            {config.environment === 'production' ? (
              <InisisAuthButton 
                $variant='fill'
                onSuccess={(data: InisisAuthResult) => {
                  handleCertify(data);
                }}
                $title='간편 인증으로 본인 확인'
                $size='md'
              />
            ) : (
              <PrimaryActionButton onClick={handleTempAuthModalOpen}>
                <Shield size={20} />
                <span>간편 인증으로 본인 확인</span>
              </PrimaryActionButton>
            )}
          </AuthButtonWrapper>
        </ContentCard>
      </ContentWrapper>
    </CenteredContainer>
  );

  /**
   * 3단계: 간편 인증 후 회원가입
   */
  const renderRegistrationStep = () => (
    <CenteredContainer>
      <ContentWrapper>
        {/* 스텝 인디케이터 */}
        <StepIndicator>
          <StepItem $isActive={true}>
            <StepCircle $isActive={true}><Check size={14} /></StepCircle>
            <StepLabel $isActive={true}>본인 확인</StepLabel>
          </StepItem>
          <StepLine $isActive={true} />
          <StepItem $isActive={true} $isCurrent={true}>
            <StepCircle $isActive={true} $isCurrent={true}>2</StepCircle>
            <StepLabel $isActive={true}>정보 입력</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>3</StepCircle>
            <StepLabel $isActive={false}>기업 설정</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>4</StepCircle>
            <StepLabel $isActive={false}>완료</StepLabel>
          </StepItem>
        </StepIndicator>

        <ContentCard>
          <CardHeader>
            <CardIconWrapper>
              <User size={24} />
            </CardIconWrapper>
            <div>
              <CardTitle>{formData.name}님, 잘 부탁드립니다! 🫡</CardTitle>
              <CardDescription>
                팩트시트 사용을 위해 추가 정보를 입력해 주세요.
              </CardDescription>
            </div>
          </CardHeader>

          <FormSection>
            <FormSectionTitle>
              <Check size={16} />
              <span>인증된 정보</span>
            </FormSectionTitle>
            <VerifiedInfoGrid>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>이름</VerifiedInfoLabel>
                <VerifiedInfoValue>{formData.name}</VerifiedInfoValue>
              </VerifiedInfoItem>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>전화번호</VerifiedInfoLabel>
                <VerifiedInfoValue>{formData.phone}</VerifiedInfoValue>
              </VerifiedInfoItem>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>이메일</VerifiedInfoLabel>
                <VerifiedInfoValue>{formData.firstEmail}</VerifiedInfoValue>
              </VerifiedInfoItem>
            </VerifiedInfoGrid>
          </FormSection>

          <FormSection>
            <FormSectionTitle>
              <Building2 size={16} />
              <span>이메일 구분</span>
            </FormSectionTitle>
            <RadioGroup>
              <RadioLabel>위 이메일 주소가 회사용 계정인가요?</RadioLabel>
              <ViewSwitcher
                options={[{ label: '네, 회사용 계정입니다.' }, { label: '아니오, 개인 계정입니다.' }]}
                selectedIndex={formData.isCompanyEmail ? 0 : 1}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, isCompanyEmail: value === 0 }));
                  setEmailChecked(false);
                  if (formData.secondEmail) {
                    const duplicationError = validateEmailDuplication(formData.secondEmail);
                    setValidationErrors(prev => ({
                      ...prev,
                      secondEmail: duplicationError
                    }));
                  }
                }}
              />
            </RadioGroup>
          </FormSection>

          <FormSection>
            <FormSectionTitle>
              <Shield size={16} />
              <span>계정 설정</span>
            </FormSectionTitle>
            
            <FormInputGroup>
              <FormInputRow>
                <LabelInput
                  label={formData.isCompanyEmail ? "개인 이메일 주소" : "회사 이메일 주소"}
                  value={formData.secondEmail}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, secondEmail: value }));
                    handleValidation('secondEmail', value);
                  }}
                  placeholder="address@email.com" 
                  error={!!validationErrors.secondEmail}
                  errorMessage={validationErrors.secondEmail ? validationErrors.secondEmail : ''}
                />
                <CheckButton 
                  type="button"
                  $isChecked={emailChecked}
                  disabled={isCheckingEmail || !formData.secondEmail || !!validationErrors.secondEmail}
                  onClick={handleCheckEmail}
                >
                  {isCheckingEmail ? '확인 중...' : emailChecked ? '확인 완료' : '중복확인'}
                </CheckButton>
              </FormInputRow>

              <FormInputRow>
                <LabelInput
                  label="로그인 아이디"
                  value={formData.loginId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, loginId: value }));
                    handleValidation('loginId', value);
                  }}
                  placeholder="로그인 아이디를 입력해주세요" 
                  error={!!validationErrors.loginId}
                  errorMessage={validationErrors.loginId ? validationErrors.loginId : ''}
                />
                <CheckButton 
                  type="button"
                  $isChecked={loginIdChecked}
                  disabled={isCheckingLoginId || !formData.loginId || !!validationErrors.loginId}
                  onClick={handleCheckLoginId}
                >
                  {isCheckingLoginId ? '확인 중...' : loginIdChecked ? '확인 완료' : '중복확인'}
                </CheckButton>
              </FormInputRow>

              <FormGrid>
                <div>
                  <LabelInput
                    label="비밀번호"
                    type="password"
                    value={formData.password}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({ ...prev, password: value }));
                      handleValidation('password', value);
                    }}
                    placeholder="비밀번호를 입력하세요" 
                    error={!!validationErrors.password}
                  />
                  {validationErrors.password && (
                    <ErrorMessage>{validationErrors.password}</ErrorMessage>
                  )}
                </div>
                
                <div>
                  <LabelInput
                    label="비밀번호 확인"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({ ...prev, confirmPassword: value }));
                      handleValidation('confirmPassword', value);
                    }}
                    placeholder="비밀번호를 다시 입력하세요" 
                    error={!!validationErrors.confirmPassword}
                  />
                  {validationErrors.confirmPassword && (
                    <ErrorMessage>{validationErrors.confirmPassword}</ErrorMessage>
                  )}
                </div>
              </FormGrid>
            </FormInputGroup>
          </FormSection>
        </ContentCard>

        <NavigationButtonGroup>
          <BackButton onClick={handleBack}>
            뒤로 가기
          </BackButton>
          <PrimaryButton onClick={handleNext}>
            다음으로
            <ChevronRight size={18} />
          </PrimaryButton>
        </NavigationButtonGroup>
      </ContentWrapper>
    </CenteredContainer>
  );

  /**
   * 4단계: 회사 정보 설정
   */
  const renderCompanySettingsStep = () => (
    <CenteredContainer>
      <ContentWrapper>
        <TextCenter>
          <MainTitle>
            기업 정보 설정을 시작할게요. 🚀
          </MainTitle>
          <Description>
            팩트시트 기업 계정 개설을 위해 아래 정보를 입력해 주세요. 필요한 정보는 인증 및 원활한 서비스 제공에만 활용됩니다.
          </Description>
        </TextCenter>

        <FlexColumn>

          <Section title="로고와 영문코드 설정">
            <FormGrid>
              <div className="form-item">
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontSize: '0.9rem', 
                  fontWeight: 500, 
                  color: semanticColors.text.primary 
                }}>
                  로고 파일
                </label>
                <FileUpload
                  type="md"
                  width="100%"
                  status="idle"
                  files={companySettings.logo ? [companySettings.logo] : []}
                  accept=".png,.jpg,.jpeg,.svg"
                  maxSize={5 * 1024 * 1024}
                  onChange={(files) => {
                    handleCompanyChange('logo', files.length > 0 ? files[0] : null);
                  }}
                  label="여기에 파일을 끌어다 놓거나 클릭하세요"
                  onError={(error) => message.error(error)}
                />
              </div>
              
                <div className="form-item">
                  <LabelInput
                    label="회사코드"
                    value={companySettings.englishCode}
                    onChange={(e) => handleCompanyChange('englishCode', e.target.value)}
                    placeholder="알파벳과 숫자로 입력해주세요."
                    error={!!validationErrors.companyCode}
                    errorMessage={validationErrors.companyCode ? validationErrors.companyCode : ''}
                  />
                  <Button 
                    $size="md" 
                    $variant={companyCodeChecked ? "fill" : "outline"}
                    disabled={isCheckingCompanyCode || !companySettings.englishCode || !!validationErrors.companyCode}
                    onClick={handleCheckCompanyCode}
                    style={{ width: '100%' }}
                  >
                    {isCheckingCompanyCode ? '확인 중...' : companyCodeChecked ? '확인 완료' : '중복확인'}
                  </Button>
                  <HelperText>
                    입력하신 값은 {domain}/example 형태로 접속 주소에 사용됩니다.
                  </HelperText>
                </div>
            </FormGrid>
          </Section>

          <Section title="미리보기">
            <PreviewContent>
              <Avatar 
                sizes="lg"
                src={companySettings.logo ? URL.createObjectURL(companySettings.logo) : ''}
                alt={invitationInfo?.name || ''}
                isUser={false}
              />
              <CompanyInfo>
                <div className="company-name">
                  {invitationInfo?.name}
              </div>
                <div className="company-url">
                  {companySettings.englishCode 
                    ? `http://startup.${domain}/${companySettings.englishCode}`
                    : `http://startup.${domain}/영문코드입력필요`
                  }
              </div>
              </CompanyInfo>
            </PreviewContent>
          </Section>

          <Section title="사업자 정보 인증">
            {(() => {
              const state = isBusinessVerified === undefined ? 'upload' : getBusinessVerificationState();
              
              switch (state) {
                case 'upload':
                  return (
                    <>
                      <Description style={{ marginBottom: '1rem' }}>
                        최신 사업자등록증을 파일을 업로드하고 인증 버튼을 눌러주세요.
                      </Description>
                      
                      <FileUpload
                        type="md"
                        width="100%"
                        status={isVerifying ? 'uploading' : uploadedFile ? 'success' : 'idle'}
                        files={uploadedFile ? [uploadedFile] : []}
                        disabled={isVerifying}
                        accept=".pdf"
                        maxSize={10 * 1024 * 1024}
                        onChange={(files) => {
                          if (files.length > 0) {
                            setUploadedFile(files[0]);
                          } else {
                            setUploadedFile(null);
                            setIsBusinessVerified(false);
                          }
                        }}
                        label="여기에 파일을 끌어다 놓거나 클릭하세요"
                        onError={(error) => {
                          message.error(error);
                        }}
                      />
                      
                      <LoadingButton 
                        $size="md" 
                        $variant="fill"
                        disabled={!uploadedFile || isVerifying}
                        onClick={(e) => {
                          // 더블 클릭 방지
                          e.preventDefault();
                          e.stopPropagation();
                          if (!isVerifying) {
                            handleVerification();
                          }
                        }}
                        style={{ width: '100%', marginTop: '1rem' }}
                      >
                        {isVerifying ? '인증 중...' : '인증하기'}
                      </LoadingButton>
                    </>
                  );

                case 1: // 인증완료
                  return (
                    <>
                      <VerificationSuccessContainer>
                        <SuccessIcon />
                        <SuccessMessage>
                          사업자 정보 인증이 완료되었습니다. 이제 다음 단계로 진행해 주세요.
                        </SuccessMessage>
                      </VerificationSuccessContainer>
                      
                      {businessInfo && (
                        <BusinessInfoContainer>
                          <BusinessInfoTitle>
                            <CheckCircleFilled style={{ color: '#10b981' }} />
                            인증된 사업자 정보
                          </BusinessInfoTitle>
                          <BusinessInfoGrid>
                            <LabelInput
                              label="사업자명"
                              value={businessInfo.companyName}
                              readOnly
                            />
                            
                            <LabelInput
                              label="사업자등록번호"
                              value={businessInfo.businessNumber}
                              readOnly
                            />
                            
                            <LabelInput
                              label="대표자"
                              value={businessInfo.representative}
                              readOnly
                            />
                            
                            <LabelInput
                              label="법인등록번호"
                              value={businessInfo.corporateNumber}
                              readOnly
                            />
                            
                            <LabelInput
                              label="설립일"
                              value={businessInfo.establishedDate}
                              readOnly
                            />
                          </BusinessInfoGrid>
                        </BusinessInfoContainer>
                      )}
                    </>
                  );

                case 2: // AI 정보 부족
                  return (
                    <>
                      <Description style={{ marginBottom: '1rem', color: '#f59e0b' }}>
                        AI 분석이 완료되지 않았거나 일부 정보가 누락 혹은 오류가 있습니다. 정확한 정보를 직접 입력해주세요.
                      </Description>
                      
                      <BusinessInfoContainer>
                        <BusinessInfoTitle>
                          <EditOutlined style={{ color: '#f59e0b' }} />
                          사업자 정보 직접 입력
                        </BusinessInfoTitle>
                        <BusinessInfoGrid>
                          <LabelInput
                            label="사업자명"
                            value={businessInfo?.companyName || ''}
                            onChange={(e) => setBusinessInfo(prev => prev ? {...prev, companyName: e.target.value} : {
                              companyName: e.target.value,
                              businessNumber: '',
                              representative: '',
                              corporateNumber: '',
                              establishedDate: ''
                            })}
                            placeholder="사업자명을 입력하세요"
                          />
                          
                          <LabelInput
                            label="사업자등록번호"
                            value={businessInfo?.businessNumber || ''}
                            maxLength={12}
                            onChange={(e) => {
                              const formattedValue = formatBusinessNumber(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, businessNumber: formattedValue} : {
                                companyName: '',
                                businessNumber: formattedValue,
                                representative: '',
                                corporateNumber: '',
                                establishedDate: ''
                              });
                            }}
                            placeholder="000-00-00000"
                            errorMessage={validationErrors.businessNumber || ''}
                          />
                          
                          <LabelInput
                            label="대표자"
                            value={businessInfo?.representative || ''}
                            onChange={(e) => {
                              setBusinessInfo(prev => prev ? {...prev, representative: e.target.value} : {
                                companyName: '',
                                businessNumber: '',
                                representative: e.target.value,
                                corporateNumber: '',
                                establishedDate: ''
                              });
                            }}
                            placeholder="대표자명을 입력하세요"
                          />
                          
                          <LabelInput
                            label="법인등록번호"
                            value={businessInfo?.corporateNumber || ''}
                            maxLength={14}
                            onChange={(e) => {
                              const formattedValue = formatCorporateNumber(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, corporateNumber: formattedValue} : {
                                companyName: '',
                                businessNumber: '',
                                representative: '',
                                corporateNumber: formattedValue,
                                establishedDate: ''
                              });
                            }}
                            placeholder="123456-1234567"
                          />
                          
                          <LabelInput
                            label="설립일"
                            value={businessInfo?.establishedDate || ''}
                            maxLength={10}
                            onChange={(e) => {
                              const formattedValue = formatEstablishmentDate(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, establishedDate: formattedValue} : {
                                companyName: '',
                                businessNumber: '',
                                representative: '',
                                corporateNumber: '',
                                establishedDate: formattedValue
                              });
                            }}
                            placeholder="YYYY-MM-DD"
                          />
                        </BusinessInfoGrid>
                        
                        <Button 
                          $size="md" 
                          $variant="fill"
                          disabled={!businessInfo?.companyName || !businessInfo?.businessNumber || 
                                   !businessInfo?.representative || !businessInfo?.corporateNumber || 
                                   !businessInfo?.establishedDate}
                          onClick={() => {
                            if (businessInfo) {
                              const validation = validateBusinessInfo(businessInfo);
                              if (validation.isValid) {
                                setValidationErrors({});
                                autoCheckCompanyStatus(businessInfo);
                              } else {
                                setValidationErrors(validation.errors);
                              }
                            }
                          }}
                          style={{ width: '100%', marginTop: '1rem' }}
                        >
                          정보 확인하기
                        </Button>
                      </BusinessInfoContainer>
                    </>
                  );

                case 3: // 초대정보와 다름
                  return (
                    <>
                      <VerificationSuccessContainer>
                        <CloseCircleFilled style={{ color: '#ef4444', fontSize: '2rem' }} />
                        <SuccessMessage style={{ color: '#ef4444' }}>
                          초대된 회사 정보와 일치하지 않습니다.
                        </SuccessMessage>
                      </VerificationSuccessContainer>

                      {/* 비교 정보 표시 */}
                      <ComparisonContainer>
                        <ComparisonTitle>
                          <ExclamationCircleFilled style={{ color: '#f59e0b' }} />
                          정보 불일치 내역
                        </ComparisonTitle>
                        
                        {validationErrors.businessNumber && (
                          <ComparisonItem>
                            <ComparisonLabel>사업자등록번호</ComparisonLabel>
                            <ComparisonContent>
                              <ComparisonRow $isError={false}>
                                <ComparisonBadge $type="invited">초대된 정보</ComparisonBadge>
                                <ComparisonValue>{invitationInfo?.businessNumber}</ComparisonValue>
                              </ComparisonRow>
                              <ComparisonRow $isError={true}>
                                <ComparisonBadge $type="input">입력된 정보</ComparisonBadge>
                                <ComparisonValue $isError={true}>{businessInfo?.businessNumber}</ComparisonValue>
                              </ComparisonRow>
                            </ComparisonContent>
                          </ComparisonItem>
                        )}
                      </ComparisonContainer>
                            
                      
                      <BusinessInfoContainer>
                        <BusinessInfoTitle>
                          <ExclamationCircleFilled style={{ color: '#ef4444' }} />
                          검증 실패 정보
                        </BusinessInfoTitle>
                        <BusinessInfoGrid>
                          <LabelInput
                            label="사업자명"
                            value={businessInfo?.companyName || ''}
                            onChange={(e) => setBusinessInfo(prev => prev ? {...prev, companyName: e.target.value} : {
                              companyName: e.target.value,
                              businessNumber: '',
                              representative: '',
                              corporateNumber: '',
                              establishedDate: ''
                            })}
                            placeholder="사업자명을 입력하세요"
                          />
                          
                          <LabelInput
                            label="사업자등록번호"
                            value={businessInfo?.businessNumber || ''}
                            maxLength={12}
                            onChange={(e) => {
                              const formattedValue = formatBusinessNumber(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, businessNumber: formattedValue} : {
                                companyName: '',
                                businessNumber: formattedValue,
                                representative: '',
                                corporateNumber: '',
                                establishedDate: ''
                              });
                            }}
                            placeholder="000-00-00000"
                            errorMessage={validationErrors.businessNumber || ''}
                          />
                          
                          <LabelInput
                            label="대표자"
                            value={businessInfo?.representative || ''}
                            onChange={(e) => {
                              setBusinessInfo(prev => prev ? {...prev, representative: e.target.value} : {
                                companyName: '',
                                businessNumber: '',
                                representative: e.target.value,
                                corporateNumber: '',
                                establishedDate: ''
                              });
                            }}
                            placeholder="대표자명을 입력하세요"
                          />
                          
                          <LabelInput
                            label="법인등록번호"
                            value={businessInfo?.corporateNumber || ''}
                            maxLength={14}
                            onChange={(e) => {
                              // 법인번호인 경우 하이픈 자동 추가 (123456-1234567)
                              const formattedValue = formatCorporateNumber(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, corporateNumber: formattedValue} : {
                                companyName: '',
                                businessNumber: '',
                                representative: '',
                                corporateNumber: formattedValue,
                                establishedDate: ''
                              });
                            }}
                            placeholder="123456-1234567"
                          />
                          
                          <LabelInput
                            label="설립일"
                            value={businessInfo?.establishedDate || ''}
                            maxLength={10}
                            onChange={(e) => {
                              // 설립일인 경우 하이픈 자동 추가 (yyyy-MM-dd)
                              const formattedValue = formatEstablishmentDate(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, establishedDate: formattedValue} : {
                                companyName: '',
                                businessNumber: '',
                                representative: '',
                                corporateNumber: '',
                                establishedDate: formattedValue
                              });
                            }}
                            placeholder="YYYY-MM-DD"
                          />
                        </BusinessInfoGrid>
                        
                        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fef2f2', borderRadius: '0.5rem', border: '1px solid #fecaca' }}>
                          <p style={{ color: '#dc2626', fontSize: '0.9rem', margin: 0 }}>
                            ⚠️ 위 정보가 초대된 회사 정보와 일치하지 않습니다. 올바른 사업자등록증을 다시 업로드해주세요.
                          </p>
                        </div>
                        
                        <Button 
                          $size="md" 
                          $variant="outline"
                          onClick={() => {
                            if (businessInfo) {
                              const validation = validateBusinessInfo(businessInfo);
                              if (validation.isValid) {
                                setValidationErrors({});
                                autoCheckCompanyStatus(businessInfo);
                              } else {
                                setValidationErrors(validation.errors);
                              }
                            }
                          }}
                          style={{ width: '100%', marginTop: '1rem' }}
                        >
                          다시 인증하기
                        </Button>
                      </BusinessInfoContainer>
                    </>
                  );

                default:
                  return null;
              }
            })()}
          </Section>
        </FlexColumn>

        <LoadingModal
          isOpen={isVerifying}
          message="인증 중입니다. 잠시만 기다려주세요."
        />

        <NavigationButtons>
          <Button 
            $size="md"
            $variant="outline"
            onClick={handleBack}
          >
            뒤로 가기
          </Button>
          <Button
            $size="md"
            $variant="fill"
            $isDisabled={!validateCompanySettingsForm()}
            onClick={handleCompanySettingsNext}
          >
            다음으로
          </Button>
        </NavigationButtons>
      </ContentWrapper>
    </CenteredContainer>
  );

  /**
   * 5단계 회원가입 성공 단계 렌더링
   */
  const renderSuccessStep = () => {
    // 바로 이동하기
    const handleImmediateRedirect = () => {
      window.location.replace(`${config.startupClientUrl}/${companySettings.englishCode}/invitation/portfolio`);
    };

    return (
      <SuccessContainer>
        <SuccessContent>
          <SuccessIconWrapper>
            <PartyPopper size={48} />
          </SuccessIconWrapper>
          
          <SuccessTitle>회원가입이 완료되었습니다!</SuccessTitle>
          <SuccessDescription>
            축하합니다! 이제 AI를 이용하여 나머지 설정을 완료하겠습니다.
          </SuccessDescription>
          
          <SuccessCard>
            <SuccessCardHeader>
              <SuccessCardIcon>
                <Sparkles size={24} />
              </SuccessCardIcon>
              <SuccessCardInfo>
                <SuccessCardTitle>AI 포트폴리오 생성 준비</SuccessCardTitle>
                <SuccessCardDesc>문서 업로드 및 자동 분석 페이지로 이동합니다.</SuccessCardDesc>
              </SuccessCardInfo>
            </SuccessCardHeader>
            
            <SuccessProgressWrapper>
              <SuccessProgressBar>
                <SuccessProgressFill />
              </SuccessProgressBar>
            </SuccessProgressWrapper>
          </SuccessCard>

          <CountdownWrapper>
            <CountdownNumber>{countdown}</CountdownNumber>
            <CountdownText>초 후 자동으로 이동됩니다</CountdownText>
          </CountdownWrapper>

          <SuccessActionButton onClick={handleImmediateRedirect}>
            <span>바로 넘어가기</span>
            <ArrowRight size={18} />
          </SuccessActionButton>
        </SuccessContent>
      </SuccessContainer>
    );
  };


  /**
   * 6단계 포트폴리오 초대 완료 단계 렌더링
   */
  const renderCompletedStep = () => {
    return (
      <CompletedContainer>
        <CompletedContent>
          <CompletedIconWrapper>
            <Check size={48} />
          </CompletedIconWrapper>
          
          <CompletedTitle>포트폴리오 초대가 완료되었습니다!</CompletedTitle>
          <CompletedDescription>
            모든 설정이 성공적으로 완료되어 포트폴리오를 이용하실 수 있습니다.
          </CompletedDescription>
          
          <CompletedCard>
            <CompletedCardHeader>
              <CompletedCardIcon>
                <Rocket size={24} />
              </CompletedCardIcon>
              <CompletedCardInfo>
                <CompletedCardTitle>팩트시트 포트폴리오 준비 완료</CompletedCardTitle>
                <CompletedCardDesc>이제 투자사와 함께 성장할 준비가 되었습니다.</CompletedCardDesc>
              </CompletedCardInfo>
            </CompletedCardHeader>
            
            <CompletedFeatureGrid>
              <CompletedFeatureCard>
                <CompletedFeatureCardIcon><FileCheck size={20} /></CompletedFeatureCardIcon>
                <CompletedFeatureCardText>실시간 기업 현황 모니터링</CompletedFeatureCardText>
              </CompletedFeatureCard>
              <CompletedFeatureCard>
                <CompletedFeatureCardIcon><Building2 size={20} /></CompletedFeatureCardIcon>
                <CompletedFeatureCardText>투자사와의 원활한 소통</CompletedFeatureCardText>
              </CompletedFeatureCard>
              <CompletedFeatureCard>
                <CompletedFeatureCardIcon><Sparkles size={20} /></CompletedFeatureCardIcon>
                <CompletedFeatureCardText>데이터 기반 성장 인사이트</CompletedFeatureCardText>
              </CompletedFeatureCard>
            </CompletedFeatureGrid>
          </CompletedCard>

          <CompletedActionButton onClick={() => window.location.replace(`${config.startupClientUrl}/${companySettings.englishCode}/invitation/portfolio`)}>
            <span>포트폴리오 시작하기</span>
            <ArrowRight size={18} />
          </CompletedActionButton>

          <CompletedFooter>
            궁금한 사항이 있으시면 언제든지 고객지원팀에 연락해 주세요.
          </CompletedFooter>
        </CompletedContent>
      </CompletedContainer>
    );
  };

  /**
   * 현재 단계에 따른 컴포넌트 렌더링
   */
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderWelcomeStep();
      case 1:
        return renderInvitationConfirmStep();
      case 2:
        return renderRegistrationStep();
      case 3:
        return renderCompanySettingsStep();
      case 4:
        return renderSuccessStep();
      case 5:
        return renderCompletedStep();
      default:
        return renderWelcomeStep();
    }
  };

  useEffect(() => {
    const getInvitationInfo = async () => {      
      const response = await onboardingService.getOnboardingInfo(notificationId);
      if(response) {
        setInvitationInfo(response);
        setFormData(prev => ({
          ...prev,
          isCompanyEmail: true,
          ceoName: response.ceoName,
          ceoEmail: response.ceoEmail,
          companyName: response.name,
          businessNumber: response.businessNumber
        }));

        if(response.onboardingStatus === 'COMPLETED') {
          // 완료된 초대인 경우, 로그인 되어 있으면 바로 포트폴리오로 이동
          if(user) {
            const company = user.companyUsers.find(cu => cu.company.businessNumber === response.businessNumber);
            if(company) {
              goToPortfolioOnboarding(company.company.code, response.onboardingStatus);
              return;
            }
          }
          setCurrentStep(5);
        } else if(response.onboardingStatus === 'NOT_STARTED') {
          setCurrentStep(0);
        } else {
          checkUserLogin(response.businessNumber, response.onboardingStatus);
        }
      }
    };
    getInvitationInfo();
  }, [location.pathname, user]);

  

  // 성공 단계에서 카운트다운과 자동 이동
  useEffect(() => {
    if (currentStep === 4) { // 성공 단계일 때만 실행
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            goToPortfolioOnboarding(companySettings.englishCode);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [currentStep, companySettings.englishCode]);

  /**
   * 이메일 중복 검사
   */
  const handleCheckEmail = async () => {
    if(!formData.secondEmail) return;

    const email = formData.secondEmail;
    // 먼저 유효성 검사
    const validationError = validateEmail(email || '');
    if (validationError) {
      setValidationErrors(prev => ({
        ...prev,
        secondEmail: validationError
      }));
      return;
    }

    // 이메일 동일성 검사
    const duplicationError = validateEmailDuplication(email);
    if (duplicationError) {
      setValidationErrors(prev => ({
        ...prev,
        secondEmail: duplicationError
      }));
      return;
    }
    
    setIsCheckingEmail(true);
    
    try {
      const result = await commonService.checkDuplicateValue('email', email || '');
      
      if (result.exists) {
        setValidationErrors(prev => ({
          ...prev,
          secondEmail: '이미 사용 중인 이메일입니다.'
        }));
        setEmailChecked(false);
        message.error('이미 사용 중인 이메일입니다.');
      } else {
        setValidationErrors(prev => ({
          ...prev,
          secondEmail: null
        }));
        setEmailChecked(true);
        message.success('사용 가능한 이메일입니다.');
      }
    } catch (error) {
      console.error('이메일 중복 검사 오류:', error);
      message.error('중복 검사 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleCheckLoginId = async () => {
    if(!formData.loginId) return;
    const loginId = formData.loginId;
    try {
      const result = await commonService.checkDuplicateValue('loginId', loginId);
      
      if (result.exists) {
        setValidationErrors(prev => ({
          ...prev,
          loginId: '이미 사용 중인 아이디입니다.'
        }));
        setLoginIdChecked(false);
        message.error('이미 사용 중인 아이디입니다.');
      } else {
        setValidationErrors(prev => ({
          ...prev,
          loginId: null
        }));
        setLoginIdChecked(true);
        message.success('사용 가능한 아이디입니다.');
      }
    } catch (error) {
      console.error('아이디 중복 검사 오류:', error);
      message.error('중복 검사 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsCheckingLoginId(false);
    }
  }

  return (
    <PageContainer>
      {renderCurrentStep()}
      
      {/* 중복 사용자 모달 */}
      {showDuplicateModal && (
        <DuplicateUserModal>
          <ModalOverlay onClick={handleDuplicateModalClose} />
          <ModalContent>
            <ModalHeader>
              <ModalIcon>⚠️</ModalIcon>
              <ModalTitle>이미 가입된 사용자입니다</ModalTitle>
            </ModalHeader>
            
            <ModalBody>
              <ModalMessage>
                해당 본인인증 정보로 이미 가입된 계정이 있습니다.<br/>
                기존 계정으로 로그인하시겠습니까?
              </ModalMessage>
            </ModalBody>
            
            <ModalFooter>
              <Button
                $size="md"
                $variant="outline"
                onClick={handleDuplicateModalClose}
              >
                아니오
              </Button>
              <Button
                $size="md"
                $variant="fill"
                onClick={handleDuplicateUserLogin}
              >
                예, 로그인하기
              </Button>
            </ModalFooter>
          </ModalContent>
        </DuplicateUserModal>
      )}

      {/* 임시 간편인증 모달 */}
      {showTempAuthModal && (
        <TempAuthModal>
          <ModalOverlay onClick={handleTempAuthModalClose} />
          <ModalContent>
            <ModalHeader>
              <ModalIcon>🔐</ModalIcon>
              <ModalTitle>임시 간편인증</ModalTitle>
            </ModalHeader>
            
            <ModalBody>
              <ModalMessage>
                테스트를 위한 임시 간편인증입니다.<br/>
                이름과 핸드폰번호를 입력해주세요.
              </ModalMessage>
              
              <TempAuthInputContainer>
                <LabelInput
                  label="이름"
                  value={tempAuthName}
                  onChange={(e) => {
                    setTempAuthName(e.target.value);
                    setTempAuthNameError('');
                  }}
                  placeholder="초대된 이름을 입력하세요"
                  error={!!tempAuthNameError}
                />
                {tempAuthNameError && (
                  <TempAuthError>{tempAuthNameError}</TempAuthError>
                )}
              </TempAuthInputContainer>

              <TempAuthInputContainer>
                <LabelInput
                  label="핸드폰번호"
                  value={tempPhoneNumber}
                  onChange={(e) => {
                    const formatted = formatTempPhoneNumber(e.target.value);
                    setTempPhoneNumber(formatted);
                    setTempPhoneError('');
                  }}
                  placeholder="010-0000-0000"
                  error={!!tempPhoneError}
                />
                {tempPhoneError && (
                  <TempAuthError>{tempPhoneError}</TempAuthError>
                )}
              </TempAuthInputContainer>
            </ModalBody>
            
            <ModalFooter>
              <Button
                $size="md"
                $variant="outline"
                onClick={handleTempAuthModalClose}
              >
                취소
              </Button>
              <Button
                $size="md"
                $variant="fill"
                onClick={handleTempAuthSubmit}
              >
                인증하기
              </Button>
            </ModalFooter>
          </ModalContent>
        </TempAuthModal>
      )}
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${semanticColors.background.primary};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const WelcomeContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 30% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse at 70% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const WelcomeContent = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    flex-direction: column;
    text-align: center;
    gap: 3rem;
  }
`;

const WelcomeTextSection = styled.div`
  flex: 1;
  max-width: 580px;

  @media (max-width: 968px) {
    max-width: 100%;
  }
`;

const WelcomeInvestorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 100px;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(8px);
  
  span {
    ${typography.paragraph.p2.medium}
    color: #fff;
  }
  
  @media (max-width: 968px) {
    margin: 0 auto 1.5rem;
  }
`;

const LogoImg = styled.img`
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
  border-radius: 6px;
`;

const LogoBox = styled.div`
  width: 1.75rem;
  height: 1.75rem;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  span {
    color: white;
    font-weight: 700;
    font-size: 0.75rem;
  }
`;

const waveAnimation = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(20deg); }
  75% { transform: rotate(-20deg); }
`;

const WelcomeWave = styled.span`
  display: inline-block;
  animation: ${waveAnimation} 1.5s ease-in-out infinite;
  transform-origin: 70% 70%;
  font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji', sans-serif;
`;

const WelcomeTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 1.5rem;
  line-height: 1.3;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const WelcomeHighlight = styled.span`
  background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const WelcomeDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
  line-height: 1.7;

  strong {
    color: #fff;
  }
`;

const WelcomeFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 2.5rem;
  
  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const WelcomeFeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  
  span {
    ${typography.caption.caption1.medium}
    color: rgba(255, 255, 255, 0.8);
  }
`;

const WelcomeFeatureIcon = styled.div`
  color: #60a5fa;
`;

const WelcomeStartButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  border: none;
  border-radius: 12px;
  color: #fff;
  ${typography.paragraph.p1.medium}
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(59, 130, 246, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// 플로팅 애니메이션
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

// 글로우 펄스 애니메이션
const glowPulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
`;

const WelcomeImageSection = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  
  @media (max-width: 968px) {
    min-height: 300px;
    width: 100%;
  }
`;

const WelcomeIllustration = styled.div`
  position: relative;
  width: 100%;
  max-width: 450px;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WelcomeGlowOrb = styled.div<{ $delay: number }>`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%);
  animation: ${glowPulse} 4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  &:nth-child(1) {
    top: 10%;
    left: 10%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%);
  }
  
  &:nth-child(2) {
    bottom: 20%;
    right: 10%;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%);
  }
  
  &:nth-child(3) {
    top: 40%;
    right: 30%;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%);
  }
`;

const WelcomeIconGrid = styled.div`
  display: flex;
  gap: 2rem;
  position: relative;
  z-index: 1;
`;

const WelcomeFloatingIcon = styled.div<{ $delay: number }>`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  backdrop-filter: blur(8px);
  color: #fff;
  animation: ${floatAnimation} 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay * 0.5}s;
  
  &:nth-child(2) {
    margin-top: 40px;
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.3);
  }
  
  &:nth-child(3) {
    margin-top: -20px;
    background: rgba(16, 185, 129, 0.2);
    border-color: rgba(16, 185, 129, 0.3);
  }
`;

const CenteredContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 2rem;
  background: ${semanticColors.background.primary};
`;

const ContentWrapper = styled.div`
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// 스텝 인디케이터
const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const StepItem = styled.div<{ $isActive?: boolean; $isCurrent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StepCircle = styled.div<{ $isActive?: boolean; $isCurrent?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${typography.paragraph.p2.medium}
  transition: all 0.3s ease;
  
  background: ${props => 
    props.$isActive 
      ? semanticColors.background.brand 
      : semanticColors.background.secondary};
  color: ${props => 
    props.$isActive 
      ? '#fff' 
      : semanticColors.text.tertiary};
  border: 2px solid ${props => 
    props.$isActive 
      ? semanticColors.background.brand 
      : semanticColors.border.primary};
  
  ${props => props.$isCurrent && `
    box-shadow: 0 0 0 4px ${semanticColors.background.brand}20;
  `}
`;

const StepLabel = styled.span<{ $isActive?: boolean }>`
  ${typography.caption.caption1.default}
  color: ${props => props.$isActive ? semanticColors.text.primary : semanticColors.text.tertiary};
  white-space: nowrap;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

const StepLine = styled.div<{ $isActive?: boolean }>`
  width: 40px;
  height: 2px;
  margin: 0 0.75rem;
  background: ${props => 
    props.$isActive 
      ? semanticColors.background.brand 
      : semanticColors.border.primary};
  transition: background 0.3s ease;
  
  @media (max-width: 640px) {
    width: 24px;
  }
`;

// 콘텐츠 카드
const ContentCard = styled.div`
  background: ${semanticColors.background.secondary};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
`;

const CardIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.brand}10;
  border-radius: 12px;
  color: ${semanticColors.background.brand};
  flex-shrink: 0;
`;

const CardTitle = styled.h2`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin: 0 0 0.25rem 0;
`;

const CardDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

// 초대 정보 카드
const InvitationInfoCard = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const InvitationInfoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 1rem;
  
  svg {
    color: ${semanticColors.background.brand};
  }
`;

const InvitationInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InvitationInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InvitationInfoLabel = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const InvitationInfoValue = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

const InvitedUserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const InvitedUserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 8px;
`;

const InvitedUserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${semanticColors.background.brand};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  ${typography.caption.caption1.medium}
  flex-shrink: 0;
`;

const InvitedUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

const InvitedUserName = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

const InvitedUserEmail = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AuthButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const PrimaryActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: ${semanticColors.background.brand};
  border: none;
  border-radius: 10px;
  color: #fff;
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

// 폼 섹션
const FormSection = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

const FormSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 1rem;
  
  svg {
    color: ${semanticColors.background.brand};
  }
`;

const VerifiedInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const VerifiedInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const VerifiedInfoLabel = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const VerifiedInfoValue = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

const FormInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormInputRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  
  > div:first-child {
    flex: 1;
  }
`;

const CheckButton = styled.button<{ $isChecked?: boolean }>`
  padding: 0.625rem 1rem;
  margin-top: 1.75rem;
  border: 1px solid ${props => 
    props.$isChecked ? semanticColors.background.success.primary : semanticColors.background.brand};
  border-radius: 8px;
  background: ${props => 
    props.$isChecked ? semanticColors.background.success.primary : semanticColors.background.brand};
  color: #fff;
  ${typography.paragraph.p2.medium}
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 90px;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 네비게이션 버튼 그룹
const NavigationButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  
  @media (max-width: 640px) {
    flex-direction: column-reverse;
  }
`;

const BackButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  background: transparent;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.secondary};
    border-color: ${semanticColors.border.secondary};
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${semanticColors.background.brand};
  border: none;
  border-radius: 10px;
  color: #fff;
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

const TextCenter = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const MainTitle = styled.h1`
  ${typography.heading.h4}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Description = styled.p`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoItem = styled.div``;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 640px) {
    width: 100%;
    flex-direction: column;
  }
`;

const StyledForm = styled.div`
  .form-item {
    margin-bottom: 1.5rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const RadioGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const RadioLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const UploadContainer = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 2rem;
  text-center;
  background-color: #fafafa;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    background-color: #f8faff;
  }

  .ant-upload {
    display: block;
    width: 100%;
  }
`;

const PreviewContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 8px;
`;

const CompanyLogo = styled.div`
  width: 3rem;
  height: 3rem;
  background-color: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    color: white;
    font-weight: bold;
    font-size: 0.875rem;
  }
`;

const CompanyInfo = styled.div`
  .company-name {
    font-weight: 600;
    color: #1f2937;
  }

  .company-url {
    font-size: 0.875rem;
    color: #6b7280;
  }
`;

const HelperText = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const LoadingButton = styled(Button)`
  position: relative;
  
  &:disabled {
    opacity: 0.7;
  }
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const VerificationSuccessContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #d1fae5;
  border: 1px solid #34d399;
  border-radius: 8px;
`;

const SuccessIcon = styled(CheckCircleFilled)`
  color: #10b981;
  font-size: 1.25rem;
`;

const SuccessMessage = styled.div`
  color: #065f46;
  font-weight: 500;
`;

const BusinessInfoContainer = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const BusinessInfoTitle = styled.h3`
  color: ${semanticColors.text.primary};
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BusinessInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;


const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

// 성공 화면 스타일
const SuccessContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 50% 30%, rgba(139, 92, 246, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SuccessContent = styled.div`
  max-width: 500px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const successIconPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 20px rgba(139, 92, 246, 0); }
`;

const SuccessIconWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  border-radius: 50%;
  color: #fff;
  animation: ${successIconPulse} 2s ease-in-out infinite;
`;

const SuccessTitle = styled.h1`
  ${typography.heading.h3}
  color: #fff;
  margin-bottom: 0.75rem;
`;

const SuccessDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
`;

const SuccessCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(8px);
`;

const SuccessCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SuccessCardIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  color: #c4b5fd;
`;

const SuccessCardInfo = styled.div`
  text-align: left;
`;

const SuccessCardTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: #fff;
  margin: 0;
`;

const SuccessCardDesc = styled.p`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const SuccessProgressWrapper = styled.div`
  padding-top: 0.5rem;
`;

const SuccessProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
`;

const progressAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const SuccessProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #8b5cf6 0%, #c4b5fd 100%);
  border-radius: 3px;
  animation: ${progressAnimation} 3s ease-in-out infinite;
`;

const CountdownWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const countdownPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const CountdownNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #c4b5fd;
  animation: ${countdownPulse} 1s ease-in-out infinite;
`;

const CountdownText = styled.div`
  ${typography.paragraph.p2.default}
  color: rgba(255, 255, 255, 0.6);
`;

const SuccessActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #fff;
  border: none;
  border-radius: 12px;
  color: #1e1b4b;
  ${typography.paragraph.p1.medium}
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.2);
  }
`;

// 완료 화면 스타일
const CompletedContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: linear-gradient(135deg, #064e3b 0%, #065f46 50%, #064e3b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 50% 30%, rgba(16, 185, 129, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const CompletedContent = styled.div`
  max-width: 500px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const completedIconPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 20px rgba(16, 185, 129, 0); }
`;

const CompletedIconWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  border-radius: 50%;
  color: #fff;
  animation: ${completedIconPulse} 2s ease-in-out infinite;
`;

const CompletedTitle = styled.h1`
  ${typography.heading.h3}
  color: #fff;
  margin-bottom: 0.75rem;
`;

const CompletedDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
`;

const CompletedCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(8px);
`;

const CompletedCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const CompletedCardIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  color: #6ee7b7;
`;

const CompletedCardInfo = styled.div`
  text-align: left;
`;

const CompletedCardTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: #fff;
  margin: 0;
`;

const CompletedCardDesc = styled.p`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const CompletedFeatureGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CompletedFeatureCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const CompletedFeatureCardIcon = styled.div`
  color: #6ee7b7;
`;

const CompletedFeatureCardText = styled.span`
  ${typography.paragraph.p2.default}
  color: rgba(255, 255, 255, 0.8);
`;

const CompletedActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #fff;
  border: none;
  border-radius: 12px;
  color: #064e3b;
  ${typography.paragraph.p1.medium}
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.2);
  }
`;

const CompletedFooter = styled.p`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.5);
`;

// Legacy styles for backwards compatibility
const CompletedFeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;


// 중복 사용자 모달 스타일 컴포넌트
const DuplicateUserModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  position: relative;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 400px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
  animation: modalEnter 0.3s ease-out;

  @keyframes modalEnter {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem;
  text-align: center;
`;

const ModalIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 0 2rem 1rem;
`;

const ModalMessage = styled.p`
  color: #4b5563;
  line-height: 1.6;
  text-align: center;
  margin: 0;
`;

const ModalFooter = styled.div`
  padding: 1rem 2rem 2rem;
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

// 임시 간편인증 모달 스타일 컴포넌트
const TempAuthModal = styled(DuplicateUserModal)``;

const TempAuthInputContainer = styled.div`
  margin-top: 1.5rem;
`;

const TempAuthError = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

// 비교 정보 스타일드 컴포넌트들
const ComparisonContainer = styled.div`
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 1.5rem;
`;

const ComparisonTitle = styled.h3`
  color: #991b1b;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ComparisonItem = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ComparisonLabel = styled.div`
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
`;

const ComparisonContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ComparisonRow = styled.div<{ $isError: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: ${props => props.$isError ? '#fee2e2' : '#f0f9ff'};
  border: 1px solid ${props => props.$isError ? '#fca5a5' : '#bae6fd'};
`;

const ComparisonBadge = styled.span<{ $type: 'invited' | 'input' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 80px;
  justify-content: center;
  
  ${props => props.$type === 'invited' ? `
    background-color: #dbeafe;
    color: #1e40af;
    border: 1px solid #93c5fd;
  ` : `
    background-color: #fee2e2;
    color: #991b1b;
    border: 1px solid #fca5a5;
  `}
`;

const ComparisonValue = styled.div<{ $isError?: boolean }>`
  flex: 1;
  font-weight: 500;
  color: ${props => props.$isError ? '#dc2626' : '#1f2937'};
  font-size: 0.9rem;
`;

export default InvitationPortfolioPage; 